/**
 * take-preview-engine.ts
 *
 * Motor de preview que roda a animação de câmera no canvas REAL
 * sem gravar vídeo.
 *
 * Integração no render():
 *
 *   if (window.previewingTakes) {
 *     window.TakePreview?.tick(deltaTime);
 *     renderer.render(scene, camera);
 *     return;
 *   }
 */

import type { AnimationPreset } from './take-types'
import { resolveFrame, getTotalDuration } from '../../../lib/animation-engine'

type TickCallback = (currentTime: number, totalDuration: number) => void
type EndCallback  = () => void

interface PreviewOptions {
  preset:  AnimationPreset
  onTick?: TickCallback
  onEnd?:  EndCallback
}

class TakePreviewEngine {
  private preset:      AnimationPreset | null = null
  private currentTime: number = 0
  private totalDur:    number = 0
  private running:     boolean = false
  private onTick?:     TickCallback
  private onEnd?:      EndCallback

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private fadePlane: any = null

  // ── Fade plane ────────────────────────────────────────────────────────────

  private ensureFadePlane() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    if (this.fadePlane || !w.camera || !w.scene) return

    // Usa o THREE já carregado pelo app — sem import direto
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const T = w.THREE
    if (!T) { console.warn('[TakePreview] window.THREE não disponível'); return }

    const geo = new T.PlaneGeometry(10, 10)
    const mat = new T.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const plane = new T.Mesh(geo, mat) as any
    plane.name = 'preview-fade-plane'
    plane.renderOrder = 9999
    plane.frustumCulled = false
    plane.position.set(0, 0, -0.5)
    plane.visible = false
    w.camera.add(plane)
    this.fadePlane = plane
  }

  private setFadeOpacity(opacity: number) {
    if (!this.fadePlane) return
    const clamped = Math.max(0, Math.min(1, opacity))
    this.fadePlane.material.opacity = clamped
    this.fadePlane.visible = clamped > 0.001
  }

  private disposeFadePlane() {
    if (!this.fadePlane) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    if (w.camera) w.camera.remove(this.fadePlane)
    this.fadePlane.geometry?.dispose()
    this.fadePlane.material?.dispose()
    this.fadePlane = null
  }

  // ── Public API ────────────────────────────────────────────────────────────

  start(opts: PreviewOptions, fromTime = 0) {
    this.preset      = opts.preset
    this.totalDur    = getTotalDuration(opts.preset)
    this.currentTime = Math.max(0, Math.min(fromTime, this.totalDur))
    this.onTick      = opts.onTick
    this.onEnd       = opts.onEnd
    this.running     = true

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).previewingTakes = true

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    if (w.controls) w.controls.enabled = false

    this.ensureFadePlane()
    this._applyFrame(this.currentTime)
  }

  stop() {
    this.running = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).previewingTakes = false

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    if (w.controls) w.controls.enabled = true

    this.setFadeOpacity(0)
  }

  dispose() {
    this.stop()
    this.disposeFadePlane()
  }

  seekTo(time: number) {
    if (!this.preset) return
    this.currentTime = Math.max(0, Math.min(time, this.totalDur))
    this.ensureFadePlane()
    this._applyFrame(this.currentTime)
    this.onTick?.(this.currentTime, this.totalDur)
  }

  tick(deltaTime: number) {
    if (!this.running || !this.preset) return

    this.currentTime += deltaTime

    if (this.currentTime >= this.totalDur) {
      this.currentTime = this.totalDur
      this._applyFrame(this.currentTime)
      this.onTick?.(this.currentTime, this.totalDur)
      this.stop()
      this.onEnd?.()
      return
    }

    this._applyFrame(this.currentTime)
    this.onTick?.(this.currentTime, this.totalDur)
  }

  get isRunning() { return this.running }
  get time()      { return this.currentTime }

  // ── Frame application ─────────────────────────────────────────────────────

  private _applyFrame(t: number) {
    if (!this.preset) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    const frame = resolveFrame(this.preset, t)

    if (w.camera) {
      w.camera.position.set(frame.position.x, frame.position.y, frame.position.z)
      w.camera.lookAt(frame.lookAt.x, frame.lookAt.y, frame.lookAt.z)
      if (Math.abs(w.camera.fov - frame.fov) > 0.01) {
        w.camera.fov = frame.fov
        w.camera.updateProjectionMatrix()
      }
    }

    if (w.rectLight1) {
      w.rectLight1.intensity = frame.lightOn ? frame.lightIntensity : 0
      w.rectLight1.color?.set(frame.lightColor)
    }

    if (w.ambLight) {
      w.ambLight.intensity = frame.ambientIntensity
    }

    if (w.renderer) {
      w.renderer.toneMappingExposure = frame.exposure
    }

    this.setFadeOpacity(frame.fadeOpacity)
  }
}

const engine = new TakePreviewEngine()

if (typeof window !== 'undefined') {
  ;(window as any).TakePreview = engine
}

export { engine as TakePreview }